import type { LensCard } from '~/types/leLens';

import { parseGrain } from '../lensGrain';

/**
 * Every card on the board that reads this dataset and names this column
 * somewhere in its view: a shelf, the Columns list, a where filter, or a
 * ratio's numerator or denominator.
 *
 * A date shelf can hold a grain expression ("week(transaction_date)") rather than the bare
 * column, so x/series/value are compared against the column `parseGrain` reads out of them: a
 * card grouping by week still counts, and still hover-highlights, as using `transaction_date`.
 *
 * Shared by the field list's "used by N" count and the field-hover highlight,
 * so a hover always lights up exactly the cards the count promised.
 */
export function cardsUsingField(datasetId: string, column: string, cards: ReadonlyArray<LensCard>): Array<LensCard> {
  return cards.filter((card) => {
    if (card.datasetId !== datasetId || !card.view) {
      return false;
    }
    const view = card.view;
    const named = (value: string | undefined) => Boolean(value) && (parseGrain(value)?.column ?? value) === column;
    if (named(view.x) || named(view.series) || named(view.value)) {
      return true;
    }
    if (view.values?.includes(column)) {
      return true;
    }
    if (view.where?.some((clause) => clause.column === column)) {
      return true;
    }
    if (view.ratio && (view.ratio.of === column || view.ratio.per === column)) {
      return true;
    }
    return false;
  });
}

export function countFieldUsage(datasetId: string, column: string, cards: ReadonlyArray<LensCard>): number {
  return cardsUsingField(datasetId, column, cards).length;
}
